import { Box, FormControl, FormHelperText } from "@mui/material";
import { Controller } from "react-hook-form";
import { SubmitButton } from "../SubmitButton";
import { FormLabel, Input, Password } from "../../../../components";
import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { PasswordRequirements } from "../PasswordRequirements";
import { TErrors, useSignUp } from "../../hooks/useSignUp";

type TSignUUpFormProps = {
  onSuccess: (email: string) => void;
};

export const SignUpForm = ({ onSuccess }: TSignUUpFormProps) => {
  const {
    errors,
    control,
    isSubmitting,
    recaptchaRef,
    setRecaptchaToken,
    onSubmit,
  } = useSignUp({ onSuccess });

  return (
    <Box component="form" width="100%" onSubmit={onSubmit} noValidate>
      <Controller
        control={control}
        name="fullName"
        render={({ field, fieldState }) => (
          <FormControl error={fieldState.invalid} sx={{ width: "100%" }}>
            <FormLabel htmlFor="fullName">Full Name</FormLabel>
            <Input
              {...field}
              fullWidth
              disabled={isSubmitting}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              id="fullName"
              type="text"
              placeholder="Enter your full name"
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState }) => (
          <FormControl
            error={fieldState.invalid}
            sx={{ width: "100%", mt: 2.5 }}
          >
            <FormLabel htmlFor="email">Email</FormLabel>
            <Input
              {...field}
              fullWidth
              disabled={isSubmitting}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              id="email"
              type="email"
              placeholder="Enter your email"
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="password"
        render={({ field, fieldState }) => (
          <FormControl
            error={fieldState.invalid}
            sx={{ width: "100%", mt: 2.5 }}
          >
            <FormLabel
              renderTooltipContent={() => <PasswordRequirements />}
              htmlFor="password"
            >
              Password
            </FormLabel>
            <Password
              {...field}
              fullWidth
              disabled={isSubmitting}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              id="password"
              placeholder="Enter your password"
            />
          </FormControl>
        )}
      />
      <Box my={3.5}>
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
          onChange={setRecaptchaToken}
        />
        {!!errors.recaptchaRequired && (
          <FormHelperText error>{errors.recaptchaRequired}</FormHelperText>
        )}
      </Box>
      <SubmitButton
        type="submit"
        disabled={isSubmitting}
        loading={isSubmitting}
      >
        Sign Up
      </SubmitButton>

      {Object.entries(errors).map(([key, error]) => {
        if (!error || (key as keyof TErrors) === "recaptchaRequired")
          return null;
        return (
          <FormHelperText error key={key}>
            {error}
          </FormHelperText>
        );
      })}
    </Box>
  );
};
